"use client";

import { ArrowTopRightOnSquareIcon, BellIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { FC, useEffect, useState } from "react";
import { NEWS_ITEMS, NewsItem } from "../const/newsItems";
import {
  markNewsSeen,
  readSeenNewsIds,
  SEEN_NEWS_KEY,
  unreadNewsIds,
} from "../helpers/newsBellExperiment";
import { useNewsBellVariant } from "../hooks/useNewsBellVariant";
import Button from "./Button";
import DropdownMenu from "./DropdownMenu";
import Typography from "./Typography";

// The header bell of the "news-bell" experiment (helpers/newsBellExperiment.ts):
// nothing for control, or until the arm is decided.
const NewsMenu: FC = () => {
  const variant = useNewsBellVariant();
  if (variant !== "test") return null;
  return <NewsBell items={NEWS_ITEMS} />;
};

// A bell with the number of unread items. Opening it lists `items` and marks
// them all read; each item opens its page in a new tab, so the app stays open.
export const NewsBell: FC<{ items: NewsItem[] }> = ({ items }) => {
  const posthog = usePostHog();
  const [open, setOpen] = useState(false);
  // Read after mounting, so a server render (no storage) matches hydration.
  const [seenIds, setSeenIds] = useState<string[] | null>(null);
  // Unread when the panel was opened: they keep a dot while it stays open.
  const [freshIds, setFreshIds] = useState<string[]>([]);

  useEffect(() => {
    setSeenIds(readSeenNewsIds());
    // Opening the bell in another tab clears the badge here too.
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === SEEN_NEWS_KEY)
        setSeenIds(readSeenNewsIds());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const unreadIds = seenIds ? unreadNewsIds(items, seenIds) : [];

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) return;
    posthog?.capture("News Bell Opened", {
      unread_count: unreadIds.length,
      unread_ids: unreadIds,
    });
    setFreshIds(unreadIds);
    if (unreadIds.length) setSeenIds(markNewsSeen(items.map(({ id }) => id)));
  };

  return (
    <DropdownMenu
      size="l"
      positions={["bottom"]}
      align="end"
      padding={8}
      boundaryInset={12}
      clickOutsideCapture
      onClickOutside={() => setOpen(false)}
      open={open}
      onOpenChange={onOpenChange}
      containerClassName="!ml-0 max-w-full w-[min(380px,calc(100vw-1.5rem))]"
    >
      <DropdownMenu.Trigger>
        <Button
          variant="tertiary"
          size="m"
          aria-label={
            unreadIds.length
              ? `What's new, ${unreadIds.length} unread`
              : "What's new"
          }
        >
          <BellIcon />
          {unreadIds.length > 0 && (
            <Button.Content>
              <span
                aria-hidden
                className="pointer-events-none absolute top-0.5 right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-4 text-center ring-2 ring-white dark:ring-background"
              >
                {unreadIds.length}
              </span>
            </Button.Content>
          )}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        disablePadding
        className="flex min-h-0 w-full max-h-[min(560px,calc(100dvh-5rem))] flex-col overflow-hidden"
      >
        {({ close }) => (
          <>
            <div className="shrink-0 px-4 pt-4 pb-3 border-b border-border">
              <Typography variant="subtitle2" component="h2">
                What&apos;s new
              </Typography>
            </div>
            <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2 flex flex-col gap-1">
              {items.map((item, index) => (
                <li key={item.id}>
                  <Link
                    href={item.href}
                    target="_blank"
                    className="group flex gap-3 rounded-xl p-3 outline-none hover:bg-surface-secondary focus-visible:bg-surface-secondary"
                    onClick={() => {
                      posthog?.capture("News Item Clicked", {
                        news_id: item.id,
                        href: item.href,
                        position: index + 1,
                        was_unread: freshIds.includes(item.id),
                      });
                      close();
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <Typography
                        variant="subtitle3"
                        component="div"
                        className="flex items-center gap-2 text-foreground"
                      >
                        {item.title}
                        {freshIds.includes(item.id) && (
                          <>
                            <span
                              aria-hidden
                              className="w-2 h-2 shrink-0 rounded-full bg-red-500"
                            />
                            <span className="sr-only">New</span>
                          </>
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="div"
                        className="mt-0.5 text-foreground-secondary"
                      >
                        {item.body}
                      </Typography>
                    </div>
                    <ArrowTopRightOnSquareIcon
                      aria-hidden
                      className="w-5 h-5 shrink-0 mt-0.5 text-foreground-muted group-hover:text-foreground"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default NewsMenu;
